import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Clock, MapPin, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { FadeUp, Overline, MaskedLine } from "../components/Reveal";
import { SERVICES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", phone: "", service: "", budget: "", timeline: "", message: "", captcha_answer: "" };
const BUDGETS = ["< $5k", "$5k – $15k", "$15k – $50k", "$50k+", "Not sure yet"];
const TIMELINES = ["As soon as possible", "Within 1–3 months", "Within 3–6 months", "Flexible / not sure yet"];

const inputCls =
  "w-full bg-white border border-ink/10 rounded-lg px-4 py-3.5 text-ink placeholder:text-slate-500 text-sm transition-colors duration-300 focus:border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/30";

const Field = ({ label, children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="block mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
    {children}
  </label>
);

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(null);

  const fetchCaptcha = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/captcha`);
      setCaptcha(res.data);
    } catch (e) {
      toast.error("Could not load captcha. Please refresh the page.");
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!captcha) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, captcha_id: captcha.captcha_id });
      toast.success("Message sent — we'll get back to you within 24 hours.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
      fetchCaptcha();
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Your name *" htmlFor="contact-name">
          <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Ada Lovelace" className={inputCls} />
        </Field>
        <Field label="Email *" htmlFor="contact-email">
          <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="ada@company.com" className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Phone number" htmlFor="contact-phone">
          <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 1234" className={inputCls} />
        </Field>
        <Field label="Company" htmlFor="contact-company">
          <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Acme Inc." className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="What do you need?" htmlFor="contact-service">
          <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
            <option value="">Select a service</option>
            {SERVICES.map((s) => (
              <option key={s.title} value={s.title}>{s.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Budget range" htmlFor="contact-budget">
          <select id="contact-budget" data-testid="contact-budget-select" value={form.budget} onChange={set("budget")} className={inputCls}>
            <option value="">Select a range</option>
            {BUDGETS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="When do you need it?" htmlFor="contact-timeline">
        <select id="contact-timeline" data-testid="contact-timeline-select" value={form.timeline} onChange={set("timeline")} className={inputCls}>
          <option value="">Select a timeline</option>
          {TIMELINES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </Field>
      <Field label="Tell us about the project *" htmlFor="contact-message">
        <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="What are you building, and what does success look like?" className={`${inputCls} resize-none`} />
      </Field>

      <div className="flex items-end gap-4">
        <div className="flex-1">
          <span className="block mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            <ShieldCheck className="inline w-3.5 h-3.5 mr-1 -mt-0.5" style={{ color: "#0D9488" }} />
            Quick check — <span data-testid="captcha-question" className="text-ink font-medium">{captcha ? captcha.question : "…"}</span> *
          </span>
          <input
            data-testid="captcha-input"
            required
            inputMode="numeric"
            value={form.captcha_answer}
            onChange={set("captcha_answer")}
            placeholder="Your answer"
            className={inputCls}
          />
        </div>
        <button
          type="button"
          data-testid="captcha-refresh-button"
          onClick={fetchCaptcha}
          aria-label="New captcha question"
          className="h-[46px] w-[46px] shrink-0 rounded-lg border border-ink/10 bg-white flex items-center justify-center text-slate-500 transition-colors duration-300 hover:border-magenta hover:text-magenta"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={sending || !captcha}
        className="group inline-flex items-center gap-2 text-white font-semibold rounded-full pl-7 pr-6 py-4 bg-[linear-gradient(100deg,#FF8A00,#E0146B,#0096C7)] bg-[length:200%_100%] bg-left transition-[background-position] duration-500 hover:bg-right disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {sending ? (
          <>Sending <Loader2 className="w-5 h-5 animate-spin" /></>
        ) : (
          <>Send message <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" /></>
        )}
      </button>
    </form>
  );
};

export default function ContactPage() {
  return (
    <>
      <section data-testid="contact-hero" className="pt-48 pb-16 md:pb-24 relative overflow-hidden">
        <div className="aurora aurora-float w-[22rem] h-[22rem] -top-10 -right-16 opacity-25 -z-10" style={{ background: "#E0146B" }} aria-hidden="true" />
        <div className="aurora aurora-float w-[18rem] h-[18rem] bottom-0 -left-16 opacity-20 -z-10" style={{ background: "#FF8A00", animationDelay: "5s" }} aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <MaskedLine delay={0.15}>
            <Overline>Contact</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-display font-black text-[11vw] md:text-[6.8vw] leading-[0.92] tracking-tighter" data-testid="contact-heading">
            <MaskedLine delay={0.3}>LET'S BUILD</MaskedLine>
            <MaskedLine delay={0.45} innerClassName="brand-gradient-text">YOUR TOMORROW.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="pb-32 md:pb-44">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
          <FadeUp className="md:col-span-7">
            <ContactForm />
          </FadeUp>

          <FadeUp delay={0.15} className="md:col-span-5">
            <div className="bg-white border border-ink/10 rounded-2xl p-8 md:p-10 space-y-10">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Prefer email?</p>
                <a
                  href="mailto:inubaanlabs@gmail.com"
                  data-testid="contact-email-link"
                  className="group mt-4 flex items-center gap-3 text-ink text-lg font-semibold transition-colors duration-300 hover:text-magenta"
                >
                  <Mail className="w-5 h-5" style={{ color: "#E0146B" }} />
                  inubaanlabs@gmail.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 mt-0.5" style={{ color: "#FF8A00" }} />
                <div>
                  <p className="font-semibold text-ink">Response within 24 hours</p>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    Every enquiry is read by a human — usually the one who'll build your product.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5" style={{ color: "#0096C7" }} />
                <div>
                  <p className="font-semibold text-ink">Remote-first, worldwide</p>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    We collaborate across time zones with clients on three continents.
                  </p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
